import { storableError } from '../util/errors';
import { currentUserShowSuccess } from './user.duck';

// ================ Action types ================ //

export const ADD_FAVORITE_REQUEST = 'app/favorites/ADD_FAVORITE_REQUEST';
export const ADD_FAVORITE_SUCCESS = 'app/favorites/ADD_FAVORITE_SUCCESS';
export const ADD_FAVORITE_ERROR = 'app/favorites/ADD_FAVORITE_ERROR';

export const REMOVE_FAVORITE_REQUEST = 'app/favorites/REMOVE_FAVORITE_REQUEST';
export const REMOVE_FAVORITE_SUCCESS = 'app/favorites/REMOVE_FAVORITE_SUCCESS';
export const REMOVE_FAVORITE_ERROR = 'app/favorites/REMOVE_FAVORITE_ERROR';

export const UPDATE_FAVORITES_OPTIMISTIC = 'app/favorites/UPDATE_FAVORITES_OPTIMISTIC';

// ================ Reducer ================ //

const initialState = {
  addFavoriteInProgress: false,
  addFavoriteError: null,
  removeFavoriteInProgress: false,
  removeFavoriteError: null,
  optimisticFavorites: null, // Used for instant UI updates
};

export default function favoritesReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ADD_FAVORITE_REQUEST:
      return {
        ...state,
        addFavoriteInProgress: true,
        addFavoriteError: null,
      };
    case ADD_FAVORITE_SUCCESS:
      return {
        ...state,
        addFavoriteInProgress: false,
        optimisticFavorites: null, // Clear optimistic state on success
      };
    case ADD_FAVORITE_ERROR:
      return {
        ...state,
        addFavoriteInProgress: false,
        addFavoriteError: payload,
        optimisticFavorites: null, // Clear optimistic state on error
      };

    case REMOVE_FAVORITE_REQUEST:
      return {
        ...state,
        removeFavoriteInProgress: true,
        removeFavoriteError: null,
      };
    case REMOVE_FAVORITE_SUCCESS:
      return {
        ...state,
        removeFavoriteInProgress: false,
        optimisticFavorites: null, // Clear optimistic state on success
      };
    case REMOVE_FAVORITE_ERROR:
      return {
        ...state,
        removeFavoriteInProgress: false,
        removeFavoriteError: payload,
        optimisticFavorites: null, // Clear optimistic state on error
      };

    case UPDATE_FAVORITES_OPTIMISTIC:
      return {
        ...state,
        optimisticFavorites: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const addFavoriteRequest = () => ({ type: ADD_FAVORITE_REQUEST });
export const addFavoriteSuccess = () => ({ type: ADD_FAVORITE_SUCCESS });
export const addFavoriteError = error => ({
  type: ADD_FAVORITE_ERROR,
  payload: error,
  error: true,
});

export const removeFavoriteRequest = () => ({ type: REMOVE_FAVORITE_REQUEST });
export const removeFavoriteSuccess = () => ({ type: REMOVE_FAVORITE_SUCCESS });
export const removeFavoriteError = error => ({
  type: REMOVE_FAVORITE_ERROR,
  payload: error,
  error: true,
});

export const updateFavoritesOptimistic = favorites => ({
  type: UPDATE_FAVORITES_OPTIMISTIC,
  payload: favorites,
});

// ================ Thunks ================ //

export const addFavorite = listingId => (dispatch, getState, sdk) => {
  const { currentUser } = getState().user;
  if (!currentUser || !currentUser.attributes) {
    const error = new Error('User not authenticated');
    dispatch(addFavoriteError(storableError(error)));
    return Promise.reject(error);
  }

  // Get current favorites from user's public data
  const currentFavorites = currentUser.attributes?.profile?.publicData?.favoriteListings || [];

  // Add new listing ID if not already in favorites
  if (currentFavorites.includes(listingId)) {
    return Promise.resolve();
  }

  const updatedFavorites = [...currentFavorites, listingId];

  // Optimistically update UI immediately
  dispatch(updateFavoritesOptimistic(updatedFavorites));
  dispatch(addFavoriteRequest());

  // Get all existing publicData to preserve it
  const existingPublicData = currentUser.attributes?.profile?.publicData || {};

  return sdk.currentUser
    .updateProfile({
      publicData: {
        ...existingPublicData,
        favoriteListings: updatedFavorites,
      },
    })
    .then(response => {
      // Manually update the currentUser with new favorites
      const updatedUser = {
        ...currentUser,
        attributes: {
          ...currentUser.attributes,
          profile: {
            ...currentUser.attributes.profile,
            publicData: {
              ...currentUser.attributes.profile.publicData,
              favoriteListings: updatedFavorites,
            },
          },
        },
      };

      // Update current user in Redux with the new favorites
      dispatch(currentUserShowSuccess(updatedUser));
      // Clear optimistic state
      dispatch(addFavoriteSuccess());
      return response;
    })
    .catch(e => {
      // Rollback optimistic update on error
      dispatch(updateFavoritesOptimistic(currentFavorites));
      dispatch(addFavoriteError(storableError(e)));
      throw e;
    });
};

export const removeFavorite = listingId => (dispatch, getState, sdk) => {
  const { currentUser } = getState().user;
  if (!currentUser || !currentUser.attributes) {
    const error = new Error('User not authenticated');
    dispatch(removeFavoriteError(storableError(error)));
    return Promise.reject(error);
  }

  // Get current favorites from user's public data
  const currentFavorites = currentUser.attributes?.profile?.publicData?.favoriteListings || [];

  // Remove listing ID from favorites
  const updatedFavorites = currentFavorites.filter(id => id !== listingId);

  // Optimistically update UI immediately
  dispatch(updateFavoritesOptimistic(updatedFavorites));
  dispatch(removeFavoriteRequest());

  // Get all existing publicData to preserve it
  const existingPublicData = currentUser.attributes?.profile?.publicData || {};

  return sdk.currentUser
    .updateProfile({
      publicData: {
        ...existingPublicData,
        favoriteListings: updatedFavorites,
      },
    })
    .then(response => {
      // Manually update the currentUser with new favorites
      const updatedUser = {
        ...currentUser,
        attributes: {
          ...currentUser.attributes,
          profile: {
            ...currentUser.attributes.profile,
            publicData: {
              ...currentUser.attributes.profile.publicData,
              favoriteListings: updatedFavorites,
            },
          },
        },
      };

      // Update current user in Redux with the new favorites
      dispatch(currentUserShowSuccess(updatedUser));
      // Clear optimistic state
      dispatch(removeFavoriteSuccess());
      return response;
    })
    .catch(e => {
      // Rollback optimistic update on error
      dispatch(updateFavoritesOptimistic(currentFavorites));
      dispatch(removeFavoriteError(storableError(e)));
      throw e;
    });
};

// ================ Selectors ================ //

export const getFavoriteListings = state => {
  // Use optimistic favorites if available for instant UI updates
  if (state.favorites.optimisticFavorites !== null) {
    return state.favorites.optimisticFavorites;
  }

  // Otherwise use the actual favorites from user profile
  const { currentUser } = state.user;
  return currentUser?.attributes?.profile?.publicData?.favoriteListings || [];
};
